import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertConfig, TransmissionAlertParameters } from "@/models/AlertModel.ts";
import { HappenedInTheLastXDays } from "@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type AlertTransmissionSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: TransmissionAlertParameters;
};

export const AlertTransmissionSettings: React.FC<AlertTransmissionSettingsProps> = ({
  parameters,
  patientId,
  alertConfig,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();
  const updateParameters = (value: unknown, key: keyof TransmissionAlertParameters) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key as string]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t("alertConfig.conditionsSuccess", {
              alert: t(alertCategoriesTranslations.observance_transmission),
            }),
          ),
        onError: () =>
          TextToast.error(
            t("alertConfig.conditionsError", {
              alert: t(alertCategoriesTranslations.observance_transmission),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.containerForText}>
        <Typography variant="body">{t("alertConfig.noTransmission")}</Typography>
      </div>
      <HappenedInTheLastXDays
        id={`alert-config-${alertConfig.id}-days`}
        onDaysChange={days => updateParameters(days, "numberOfDays")}
        minDays={1}
        maxDays={7}
        days={parameters.numberOfDays}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `,
  containerForText: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(4)};
    flex-wrap: wrap;
  `,
});
